import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  effect,
  inject,
  input,
} from '@angular/core';

/** Durée du décompte, en millisecondes. */
const DURATION = 1200;
/**
 * Filet de sécurité, volontairement plus long que l'animation.
 *
 * Un moteur PDF sans tête fait avancer le temps virtuellement et bride
 * `requestAnimationFrame` : le compteur a déjà été photographié en vol. Cette
 * échéance écrit la valeur finale quoi qu'il arrive.
 */
const SETTLE_AFTER = 1400;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Départ rapide, arrivée douce. */
function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

/**
 * Compteur animé de 0 jusqu'à sa valeur, écrit dans le texte de l'hôte.
 *
 * Le drapeau `settled` n'est pas décoratif : sans lui, une boucle `rAF` bridée
 * continue d'écrire des valeurs intermédiaires **par-dessus** la valeur finale
 * déjà posée — c'est ainsi qu'un export PDF a imprimé la note « 7 / 20 » alors
 * qu'elle valait 9.
 *
 * La valeur est arrondie pendant le décompte aussi, pour que la note ne
 * clignote pas à travers des décimales avant d'atteindre un entier.
 */
@Directive({
  selector: '[s2eCountUp]',
  standalone: true,
})
export class ReportCountUpDirective implements OnDestroy {
  private readonly host = inject(ElementRef).nativeElement as HTMLElement;
  private readonly zone = inject(NgZone);

  readonly value = input.required<number>({ alias: 's2eCountUp' });

  /**
   * Mise en forme de la valeur affichée.
   *
   * Change avec la langue — d'où la relance de l'animation quand la fonction
   * change : un compteur figé garderait le nombre formaté dans l'ancienne
   * locale.
   */
  readonly format = input<(value: number) => string>((value) => String(value), {
    alias: 'countUpFormat',
  });

  private frame = 0;
  private timeout = 0;
  private settled = false;

  constructor() {
    effect(() => {
      const target = this.value();
      const format = this.format();
      this.restart(target, format);
    });
  }

  private restart(target: number, format: (value: number) => string): void {
    this.cancel();
    this.settled = false;

    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      this.settle(target, format);
      return;
    }

    /* Hors zone Angular : une passe de détection de changements par image, sur
       un texte que la directive écrit elle-même, ne servirait à rien. */
    this.zone.runOutsideAngular(() => {
      let start: number | null = null;

      const tick = (now: number): void => {
        if (this.settled) return;
        if (start === null) start = now;
        const progress = Math.min(1, (now - start) / DURATION);
        this.host.textContent = format(Math.round(target * easeOutCubic(progress)));
        if (progress < 1) {
          this.frame = requestAnimationFrame(tick);
        } else {
          this.settled = true;
        }
      };

      this.frame = requestAnimationFrame(tick);
      this.timeout = window.setTimeout(() => this.settle(target, format), SETTLE_AFTER);
    });
  }

  /** Pose la valeur finale et interdit toute écriture ultérieure. */
  private settle(target: number, format: (value: number) => string): void {
    this.settled = true;
    this.host.textContent = format(target);
  }

  private cancel(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.timeout) window.clearTimeout(this.timeout);
    this.frame = 0;
    this.timeout = 0;
  }

  ngOnDestroy(): void {
    this.settled = true;
    this.cancel();
  }
}
